import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import { useIsVisible } from '@sbt-web/hooks';
import type { ItemMap } from '@sbt-web/network/types';
import { Divider } from '@sbt-web/ui';
import { urnTransformer } from '@sbt-web/utils';
import { transformCategory } from '@/tools/tracking/transformer';
import { getDataLayer, getOrCreatePulse } from '@/tools/tracking/utils';
import classes from './Map.module.scss';
interface Props {
  map?: ItemMap;
}
const observerOptionsTop = {
  threshold: 0.01
};
const observerOptionsMiddle = {
  threshold: 0.5
};
const buildTrackingData = (position: string) => {
  const rawAd = getDataLayer().ads[0];
  const {
    adId,
    contentId
  } = urnTransformer(rawAd.urn) ?? {};
  return {
    type: 'View',
    intent: 'View',
    object: {
      id: 'google_map',
      type: 'UIElement',
      elementType: 'Map',
      elementSubtype: 'google',
      position
    },
    target: {
      id: adId,
      type: 'ClassifiedAd',
      category: transformCategory(rawAd.category),
      name: document.title,
      adId: adId && Number.parseInt(adId),
      contentId
    },
    page: {
      pageType: 'ad_detail',
      '@id': 'sdrn:subito:page:ad_detail',
      '@type': 'Page'
    }
  };
};
export function Map(t0) {
  const $ = _c(16);
  const {
    map
  } = t0;
  const element = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      observerOptions: observerOptionsTop
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [isVisibleTop, setElementTop, unobserveTop] = useIsVisible(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      observerOptions: observerOptionsMiddle
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [isVisibleMiddle, setElementMiddle, unobserveMiddle] = useIsVisible(t2);
  let t3;
  let t4;
  if ($[2] !== isVisibleTop || $[3] !== setElementTop || $[4] !== unobserveTop) {
    t3 = () => {
      if (element.current) {
        setElementTop(element.current);
      }
      if (isVisibleTop) {
        getOrCreatePulse()?.queueEvent(buildTrackingData("Top"));
        unobserveTop();
      }
    };
    t4 = [isVisibleTop, setElementTop, unobserveTop];
    $[2] = isVisibleTop;
    $[3] = setElementTop;
    $[4] = unobserveTop;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[7] !== isVisibleMiddle || $[8] !== setElementMiddle || $[9] !== unobserveMiddle) {
    t5 = () => {
      if (element.current) {
        setElementMiddle(element.current);
      }
      if (isVisibleMiddle) {
        getOrCreatePulse()?.queueEvent(buildTrackingData("Middle"));
        unobserveMiddle();
      }
    };
    t6 = [isVisibleMiddle, setElementMiddle, unobserveMiddle];
    $[7] = isVisibleMiddle;
    $[8] = setElementMiddle;
    $[9] = unobserveMiddle;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  useEffect(t5, t6);
  if (!map?.longitude || !map?.latitude) {
    return null;
  }
  let t7;
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Divider topSpacing="sm" bottomSpacing="xxl" />;
    t8 = <h6 className="headline-6">Posizione</h6>;
    $[12] = t7;
    $[13] = t8;
  } else {
    t7 = $[12];
    t8 = $[13];
  }
  const t9 = `https://www.google.com/maps/embed/v1/place?key=${process.env.NEXT_PUBLIC_GCP_KEY}&q=${map.latitude},${map.longitude}&zoom=14`;
  let t10;
  if ($[14] !== t9) {
    t10 = <>{t7}{t8}<div ref={element}><iframe title="Mappa posizione annuncio" className={classes.frame} loading="lazy" src={t9} allowFullScreen={true} /></div></>;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  return t10;
}